import WithdrawalQueue from "@/components/admin/WithdrawalQueue";
import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Banknote, Download, RotateCcw, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TabBtn = ({ active, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid} className={`px-3 py-1 rounded-full text-xs font-medium transition ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>{children}</button>
);

const downloadCSV = (text, filename) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const Payouts = () => {
  const [tab, setTab] = useState("queue");
  return (
    <div className="p-8">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900"><Banknote className="w-6 h-6 inline mr-2"/> Payouts</h1>
          <p className="text-sm text-slate-500 mt-1">Export a bulk-transfer CSV, track batches awaiting bank confirmation, and browse full history.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-slate-100 p-1">
          <TabBtn active={tab === "queue"}   onClick={() => setTab("queue")}   testid="tab-queue">Queue</TabBtn>
          <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="tab-pending">Pending confirmation</TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">History</TabBtn>
          <TabBtn active={tab === "withdrawals"} onClick={() => setTab("withdrawals")} testid="tab-withdrawals">Withdrawal requests</TabBtn>
        </div>
      </div>
      {tab === "queue"   && <QueueTab/>}
      {tab === "pending" && <PendingTab/>}
      {tab === "history" && <HistoryTab/>}
      {tab === "withdrawals" && <WithdrawalQueue/>}
    </div>
  );
};

const QueueTab = () => {
  const [items, setItems] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [templateName, setTemplateName] = useState("");
  const [templateCols, setTemplateCols] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [excludedSummary, setExcludedSummary] = useState(null);

  const load = async () => {
    const params = { only_verified: onlyVerified };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to = dateTo;
    const r = await adminApi.get("/payouts/queue", { params });
    setItems(r.data.items || []);
    setGrandTotal(r.data.grand_total || 0);
    setTemplateName(r.data.template_name || "");
    setTemplateCols(r.data.template_columns || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyVerified, dateFrom, dateTo]);

  const allEligibleIds = useMemo(() =>
    items.filter(i => i.bank.eligible).flatMap(i => i.deliveries.map(d => d.delivery_id)),
  [items]);

  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected]);
  const selectedTotal = useMemo(() => {
    let s = 0;
    for (const i of items) for (const d of i.deliveries) if (selected[d.delivery_id]) s += d.carrier_payout;
    return s;
  }, [items, selected]);
  const selectedCarrierCount = useMemo(() => {
    const set = new Set();
    for (const i of items) for (const d of i.deliveries) if (selected[d.delivery_id]) set.add(i.carrier_id);
    return set.size;
  }, [items, selected]);

  const toggleAll = () => {
    const allSelected = allEligibleIds.length && allEligibleIds.every(id => selected[id]);
    const next = { ...selected };
    allEligibleIds.forEach(id => { next[id] = !allSelected; });
    setSelected(next);
  };

  const doExport = async () => {
    if (!selectedIds.length) return toast.error("Pick at least one row to export.");
    setBusy(true);
    try {
      const r = await adminApi.post("/payouts/export", { delivery_ids: selectedIds });
      if (r.data.csv) downloadCSV(r.data.csv, r.data.csv_filename || "carryit-payouts.csv");
      const t = r.data.totals || {};
      toast.success(`Exported ${t.batches || 0} batch(es) · ₹${(t.amount || 0).toFixed(2)}`);
      if ((r.data.excluded || []).length) setExcludedSummary(r.data.excluded);
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">From</label>
          <Input data-testid="queue-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">To</label>
          <Input data-testid="queue-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1"/>
        </div>
        <label className="ml-2 flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox data-testid="only-verified-toggle" checked={onlyVerified} onCheckedChange={(v) => setOnlyVerified(!!v)}/>
          Show only carriers with verified bank details
        </label>
        <div className="ml-auto text-right">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Full amount to be paid</div>
          <div className="text-2xl font-display font-bold tabular-nums" data-testid="queue-grand-total">₹{grandTotal.toFixed(2)}</div>
          <div className="text-[11px] text-slate-500">Check your balance before exporting.</div>
        </div>
      </div>

      <div data-testid="queue-template-info" className="text-[11px] text-slate-500 mb-2">
        CSV template: <b>{templateName || "default"}</b> · columns: {templateCols.join(", ")}. Bank template can be changed in one place (<span className="font-mono">payout_export.py</span>).
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden mb-3">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <Checkbox
              data-testid="select-all-eligible"
              checked={allEligibleIds.length > 0 && allEligibleIds.every(id => selected[id])}
              onCheckedChange={toggleAll}
              disabled={!allEligibleIds.length}
            /> Select all eligible ({allEligibleIds.length})
          </label>
          <span className="text-xs text-slate-500">Selected: <b data-testid="selected-count">{selectedIds.length}</b> deliveries across <b>{selectedCarrierCount}</b> carrier{selectedCarrierCount === 1 ? "" : "s"} · <b data-testid="selected-total">₹{selectedTotal.toFixed(2)}</b></span>
          <Button
            data-testid="export-btn"
            onClick={doExport}
            disabled={!selectedIds.length || busy}
            className="ml-auto rounded-full bg-slate-900 hover:bg-slate-800"
          >
            <Download className="w-4 h-4 mr-1"/> Export for bank transfer
          </Button>
        </div>
        {items.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2"/>
            Nothing owed right now.
          </div>
        )}
        {items.map(it => (
          <div key={it.carrier_id} data-testid={`queue-row-${it.carrier_id}`} className={`px-4 py-3 border-b border-slate-100 flex items-start gap-3 ${it.bank.eligible ? "" : "bg-amber-50/50"}`}>
            <Checkbox
              className="mt-1"
              data-testid={`chk-${it.carrier_id}`}
              disabled={!it.bank.eligible}
              checked={it.deliveries.every(d => selected[d.delivery_id])}
              onCheckedChange={(v) => {
                const next = { ...selected };
                it.deliveries.forEach(d => { next[d.delivery_id] = !!v; });
                setSelected(next);
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <a href={`/admin/users/${it.carrier_id}`} className="font-semibold text-slate-900 hover:underline">{it.carrier.name || it.carrier.phone}</a>
                <span className="text-xs text-slate-500">{it.carrier.phone}</span>
                {it.bank.eligible
                  ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700"><ShieldCheck className="w-3 h-3 mr-1"/> Bank verified</Badge>
                  : it.bank.has_details
                    ? <Badge className="text-[10px] bg-amber-100 text-amber-700">Awaiting verification</Badge>
                    : <Badge className="text-[10px] bg-rose-100 text-rose-700">Bank details missing</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {it.carrier.account_holder_name || <em>no holder name</em>} · A/C {it.carrier.account_number || <em>—</em>} · IFSC {it.carrier.ifsc_code || <em>—</em>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-display font-bold tabular-nums">₹{it.total_owed.toFixed(2)}</div>
              <div className="text-[11px] text-slate-500">{it.count} deliver{it.count === 1 ? "y" : "ies"}</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!excludedSummary} onOpenChange={(v) => !v && setExcludedSummary(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600"/> Some carriers were excluded</DialogTitle>
            <DialogDescription>The CSV above contains the eligible rows only. The following carriers were skipped:</DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-y-auto space-y-2 text-sm">
            {(excludedSummary || []).map(e => (
              <div key={e.carrier_id} className="rounded-lg bg-amber-50 border border-amber-200 p-2.5">
                <div className="font-semibold text-slate-900">{e.carrier_name || e.carrier_phone}</div>
                <div className="text-xs text-slate-600">{e.reason}</div>
                <div className="text-[11px] text-slate-500">₹{(e.amount || 0).toFixed(2)} · {e.count} deliveries · not exported</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PendingTab = () => {
  const [rows, setRows] = useState([]);
  const [markingFor, setMarkingFor] = useState(null);
  const [revertingFor, setRevertingFor] = useState(null);
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await adminApi.get("/payouts/pending");
    setRows(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const markPaid = async () => {
    if (!reference.trim()) return toast.error("Bank transaction reference required.");
    setBusy(true);
    try {
      await adminApi.post(`/payouts/${markingFor.id}/mark-paid`, { reference: reference.trim(), paid_at: paidAt });
      toast.success(`₹${markingFor.amount.toFixed(2)} marked paid.`);
      setMarkingFor(null); setReference(""); setPaidAt(new Date().toISOString().slice(0, 10));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    if (!reason.trim()) return toast.error("Please note why this transfer failed.");
    setBusy(true);
    try {
      await adminApi.post(`/payouts/${revertingFor.id}/revert`, { reason: reason.trim() });
      toast.success("Payout reverted — deliveries are back in the queue.");
      setRevertingFor(null); setReason("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="pending-list">
        {rows.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2"/>
            No batches waiting for confirmation.
          </div>
        )}
        {rows.map(r => (
          <div key={r.id} data-testid={`pending-row-${r.id}`} className="px-4 py-3 border-b border-slate-100 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <a href={`/admin/users/${r.carrier_id}`} className="font-semibold text-slate-900 hover:underline">{r.carrier?.name || r.carrier?.phone}</a>
                <span className="text-xs text-slate-500">{r.carrier?.phone}</span>
                <Badge className="text-[10px] bg-amber-100 text-amber-700">Pending confirmation</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">Batch ref: <span className="font-mono">{r.reference}</span> · exported {r.exported_at ? new Date(r.exported_at).toLocaleString() : "—"} · {r.count} deliveries</div>
              <div className="text-xs text-slate-500">A/C {r.carrier?.account_number} · IFSC {r.carrier?.ifsc_code}</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-display font-bold tabular-nums">₹{r.amount.toFixed(2)}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid={`mark-paid-${r.id}`} onClick={() => setMarkingFor(r)} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Mark as paid</Button>
              <Button data-testid={`revert-${r.id}`} onClick={() => setRevertingFor(r)} variant="outline" className="rounded-full"><RotateCcw className="w-3.5 h-3.5 mr-1"/> Revert</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!markingFor} onOpenChange={(v) => !v && setMarkingFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Mark payout as paid</DialogTitle>
            <DialogDescription>Enter the bank transaction reference and the payment date. The carrier will see both in their earnings so they can reconcile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Bank transaction reference (UTR)</label>
              <Input data-testid="mark-paid-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. HDFCN2026090001"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Payment date</label>
              <Input data-testid="mark-paid-date" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}/>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMarkingFor(null)}>Cancel</Button>
            <Button data-testid="mark-paid-confirm" disabled={!reference.trim() || busy} onClick={markPaid} className="bg-slate-900 hover:bg-slate-800">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revertingFor} onOpenChange={(v) => !v && setRevertingFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Revert this payout</DialogTitle>
            <DialogDescription>The deliveries go back to the queue for re-export. Add a note so we know what went wrong.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Reason</label>
            <Input data-testid="revert-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. bank rejected — a/c holder mismatch"/>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRevertingFor(null)}>Cancel</Button>
            <Button data-testid="revert-confirm" disabled={!reason.trim() || busy} onClick={revert} className="bg-amber-600 hover:bg-amber-700">Revert to queue</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const HistoryTab = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");

  const load = async () => {
    const params = { page, page_size: 50 };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (status) params.status = status;
    const r = await adminApi.get("/payouts/history", { params });
    setRows(r.data.items || []);
    setTotal(r.data.total || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, dateFrom, dateTo, status]);

  const exportCSV = async () => {
    const params = { format: "csv", page_size: 500 };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (status) params.status = status;
    try {
      const r = await adminApi.get("/payouts/history", { params });
      downloadCSV(r.data.csv, r.data.filename || "carryit-payout-history.csv");
      toast.success("Accounting CSV downloaded.");
    } catch (e) { toast.error("Failed."); }
  };

  return (
    <>
      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-3 flex flex-wrap items-end gap-3">
        <div><label className="text-[11px] uppercase tracking-wider text-slate-500">From</label><Input data-testid="hist-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1"/></div>
        <div><label className="text-[11px] uppercase tracking-wider text-slate-500">To</label><Input data-testid="hist-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1"/></div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Status</label>
          <select data-testid="hist-status" value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 h-9 px-3 rounded-md border border-slate-200 text-sm">
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="pending_confirmation">Pending</option>
            <option value="reverted">Reverted</option>
          </select>
        </div>
        <Button data-testid="history-export-btn" onClick={exportCSV} variant="outline" className="ml-auto rounded-full"><Download className="w-4 h-4 mr-1"/> Export accounting CSV</Button>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">{total} payouts recorded</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">When</th>
              <th className="px-4 py-2 text-left">Carrier</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Bank ref</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-right">Deliveries</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id} data-testid={`hist-row-${r.id}`}>
                <td className="px-4 py-2 text-xs tabular-nums text-slate-600">{r.paid_at ? new Date(r.paid_at).toLocaleString() : new Date(r.exported_at || r.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs">{r.carrier?.name || "—"}<div className="text-slate-400">{r.carrier?.phone}</div></td>
                <td className="px-4 py-2"><Badge className={"text-[10px] " + (r.status === "paid" ? "bg-emerald-100 text-emerald-700" : r.status === "reverted" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700")}>{(r.status || "").replace("_", " ").toUpperCase()}</Badge></td>
                <td className="px-4 py-2 text-xs font-mono">{r.bank_reference || r.reference}</td>
                <td className="px-4 py-2 text-right font-medium tabular-nums">₹{(r.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-slate-500">{r.count}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-slate-400">Nothing paid yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>← Prev</Button>
        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={rows.length < 50}>Next →</Button>
        <div className="text-xs text-slate-400 ml-auto">Page {page}</div>
      </div>
    </>
  );
};

export default Payouts;
